/**
 * BottomNav — Barra de navegación inferior minimalista (4 tabs).
 * Patrón visual: Inicio · Solicitudes · Contratos · Chats/Pagos
 * Settings se accede desde el icono ⚙️ en el Home header.
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-solid-svg-icons/faComments';
import { faCoins } from '@fortawesome/free-solid-svg-icons/faCoins';
import { faFileLines } from '@fortawesome/free-solid-svg-icons/faFileLines';
import { faHouse } from '@fortawesome/free-solid-svg-icons/faHouse';
import { faRoute } from '@fortawesome/free-solid-svg-icons/faRoute';
import { faTruck } from '@fortawesome/free-solid-svg-icons/faTruck';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { countOperatorSuggestions } from '../marketplace/requestSuggestion';
import { useAuthStore, useChatStore, useMarketplaceStore, useProviderContextStore } from '../../stores';
import { cn } from '../../utils';

interface NavItem {
    id: string;
    label: string;
    path: string;
    activePaths?: string[];
    icon: IconDefinition;
}

/* ── Componente principal ──────────────────────────────────── */

export function BottomNav() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const operationMode = useMarketplaceStore((state) => state.operationMode);
    const feedRequests = useMarketplaceStore((state) => state.feedRequests);
    const feedMeta = useMarketplaceStore((state) => state.feedMeta);
    const isLoadingFeed = useMarketplaceStore((state) => state.isLoadingFeed);
    const fetchFeedRequests = useMarketplaceStore((state) => state.fetchFeedRequests);
    const activeProviderContext = useProviderContextStore((state) => state.activeContext);
    const chatConversationsMeta = useChatStore((state) => state.conversationsMeta);
    const isLoadingChatConversations = useChatStore((state) => state.isLoadingConversations);
    const fetchChatConversations = useChatStore((state) => state.fetchConversations);
    const unreadChatCount = useChatStore((state) => state.unreadTotal);
    const resolvedOperationMode = operationMode ?? user?.operation_mode ?? 'client';
    const canViewOperatorRequests = activeProviderContext?.effective_permissions.includes('quote.create') ?? false;
    const operatorSuggestionCount = resolvedOperationMode === 'operator' && canViewOperatorRequests
        ? countOperatorSuggestions(feedRequests)
        : 0;
    useEffect(() => {
        if (resolvedOperationMode !== 'operator' || !canViewOperatorRequests || feedMeta !== null || isLoadingFeed) {
            return;
        }

        void fetchFeedRequests(1);
    }, [resolvedOperationMode, canViewOperatorRequests, feedMeta, isLoadingFeed, fetchFeedRequests]);

    useEffect(() => {
        if (!user || chatConversationsMeta !== null || isLoadingChatConversations) {
            return;
        }

        void fetchChatConversations();
    }, [user, chatConversationsMeta, isLoadingChatConversations, fetchChatConversations]);

    const operatorFourthItem: NavItem = activeProviderContext?.role === 'driver'
        ? {
            id: 'shift',
            label: 'Jornada',
            path: '/driver/shift',
            activePaths: ['/driver/shift'],
            icon: faRoute,
        }
        : {
            id: 'payouts',
            label: 'Pagos',
            path: '/operator/payouts',
            activePaths: ['/operator/payouts', '/operator/provider-payouts'],
            icon: faCoins,
        };

    const operatorNavItems: NavItem[] = [
            {
                id: 'home',
                label: 'Inicio',
                path: '/',
                icon: faHouse,
            },
            ...(canViewOperatorRequests ? [{
                id: 'requests',
                label: 'Solicitudes',
                path: '/marketplace/feed',
                activePaths: ['/marketplace/feed', '/marketplace/requests'],
                icon: faFileLines,
            }] : []),
            {
                id: 'contracts',
                label: 'Contratos',
                path: '/marketplace/contracts',
                activePaths: ['/marketplace/contracts', '/marketplace/invitations'],
                icon: faTruck,
            },
            operatorFourthItem,
            {
                id: 'chat',
                label: 'Chats',
                path: '/chat',
                activePaths: ['/chat'],
                icon: faComments,
            },
        ];

    const navItems: NavItem[] = resolvedOperationMode === 'operator'
        ? operatorNavItems
        : [
        {
            id: 'home',
            label: 'Inicio',
            path: '/',
            icon: faHouse,
        },
        {
            id: 'requests',
            label: 'Solicitudes',
            path: '/marketplace/requests',
            activePaths: ['/marketplace/requests', '/marketplace/feed', '/marketplace/operator'],
            icon: faFileLines,
        },
        {
            id: 'contracts',
            label: 'Contratos',
            path: '/marketplace/contracts',
            activePaths: ['/marketplace/contracts', '/marketplace/invitations'],
            icon: faTruck,
        },
        {
            id: 'chat',
            label: 'Chats',
            path: '/chat',
            activePaths: ['/chat'],
            icon: faComments,
        },
        ];

    const isActive = (item: NavItem): boolean => {
        const currentPath = location.pathname;
        if (item.path === '/' && currentPath === '/') return true;
        if (item.path !== '/' && currentPath === item.path) return true;
        if (item.activePaths) {
            return item.activePaths.some((p) => currentPath.startsWith(p));
        }
        return item.path !== '/' && currentPath.startsWith(item.path);
    };

    const handleNavigate = (path: string) => {
        if (location.pathname !== path) {
            navigate(path);
        }
    };

    return (
        <nav
            className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-[var(--color-nav-bg)]"
            style={{ paddingBottom: 'var(--spacing-safe-bottom, 0px)' }}
            aria-label="Navegación principal"
        >
            <div
                className="mx-auto flex max-w-[600px] items-stretch justify-around"
                style={{ height: 'var(--bottom-nav-height)' }}
            >
                {navItems.map((item) => {
                    const active = isActive(item);

                    return (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => handleNavigate(item.path)}
                            className={cn(
                                'group relative flex min-w-[60px] flex-1 flex-col items-center justify-center gap-1 px-1 transition-colors',
                                'active:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-inset',
                                active ? 'text-[var(--color-nav-active)]' : 'text-[var(--color-nav-inactive)]',
                            )}
                            aria-label={item.label}
                            aria-current={active ? 'page' : undefined}
                        >
                            <span className="flex size-6 items-center justify-center">
                                <FontAwesomeIcon icon={item.icon} className={cn('text-[18px]', !active && 'opacity-90')} aria-hidden="true" />
                            </span>
                            {item.id === 'requests' && operatorSuggestionCount > 0 ? (
                                <span className="absolute right-[calc(50%-1.35rem)] top-1.5 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-warning px-1 text-[9px] font-bold leading-none text-black">
                                    {operatorSuggestionCount > 99 ? '99+' : operatorSuggestionCount}
                                </span>
                            ) : null}

                            {item.id === 'chat' && unreadChatCount > 0 ? (
                                <span
                                    className="absolute right-[calc(50%-1.35rem)] top-1.5 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold leading-none text-white"
                                    aria-label={`${unreadChatCount} mensajes sin leer`}
                                >
                                    {unreadChatCount > 99 ? '99+' : unreadChatCount}
                                </span>
                            ) : null}

                            <span className={cn(
                                'text-[10px] leading-none',
                                active ? 'font-semibold' : 'font-medium',
                            )}>
                                {item.label}
                            </span>

                            {active && (
                                <span className="absolute bottom-1.5 left-1/2 size-1 -translate-x-1/2 rounded-full bg-primary" />
                            )}
                        </button>
                    );
                })}
            </div>
        </nav>
    );
}
